import { useCallback, useEffect, useRef, useState } from 'react';
import { API } from '../../../../../../api';
import { getLLAuthHeaders } from '../../../../utils/llAuthHeaders';
import type { FwProgram } from './frameworkData';
import { FwAnswers, emptyAnswers, recommend } from './frameworkEngine';

// The org's Learning Framework, wherever it lives.
//
// Primary store is the BE (GET/PUT/DELETE /v3/admin/learning-framework, POST
// …/recommend, POST …/added — docs/EPIC-learning-framework-builder.md in the BE
// repo) so every admin and device in the org sees the same framework.
// generate() asks Elliott live first and falls back to the built-in engine
// (frameworkEngine.recommend) on any error, saving that result with PUT so the
// fallback is shared too. When the BE itself is unreachable (route not
// deployed, network down) the framework is kept in localStorage per org — the
// PR #584 prototype behaviour — and moves to the server on the next generate.

export interface AddedLink {
  recommendationId: string;
  groupId: string;
  name?: string;
}

export interface LearningFramework {
  answers: FwAnswers;
  // Elliott's company-specific intro; '' means "build the summary client-side".
  intro: string;
  programs: FwProgram[];
  source: 'elliott' | 'engine';
  generatedAt: number;
  added: AddedLink[];
}

export interface LearningFrameworkStore {
  framework: LearningFramework | null;
  // Initial load in flight — the Elliott menu label waits for it.
  loading: boolean;
  // Where the framework was last read from / written to.
  storage: 'server' | 'local';
  reload: () => Promise<void>;
  // Elliott live → engine fallback; persists; resolves with the framework.
  generate: (answers: FwAnswers) => Promise<LearningFramework>;
  // "Start over".
  remove: () => Promise<void>;
  // Record the programs just created from recommendations.
  markAdded: (items: AddedLink[]) => Promise<void>;
}

interface Options {
  enabled: boolean;
  orgId?: string;
  isPlatformAdmin?: boolean;
  orgName?: string;
}

// Local fallback, keyed per org so an admin working across orgs in one browser
// never sees another org's framework.
const localKey = (orgId: string) => `fw_builder_v2_${orgId}`;
const readLocal = (orgId?: string): LearningFramework | null => {
  if (!orgId) return null;
  try {
    const raw = localStorage.getItem(localKey(orgId));
    return raw ? (JSON.parse(raw) as LearningFramework) : null;
  } catch {
    return null;
  }
};
const writeLocal = (orgId: string | undefined, fw: LearningFramework | null) => {
  if (!orgId) return;
  try {
    if (fw) localStorage.setItem(localKey(orgId), JSON.stringify(fw));
    else localStorage.removeItem(localKey(orgId));
  } catch {
    /* storage full or blocked, non-fatal */
  }
};

// BE program → FwProgram (the renderer's shape). templateReady/tags are engine
// concepts the BE doesn't carry.
const mapProgram = (p: any): FwProgram => ({
  id: String(p.id),
  name: String(p.name || ''),
  description: String(p.description || ''),
  pillar: p.pillar,
  type: p.type,
  format: p.format,
  templateReady: Boolean(p.readyBuilt),
  tags: [],
  readyBuilt: Boolean(p.readyBuilt),
  basis: typeof p.basis === 'string' ? p.basis : '',
  needsLegalReview: Boolean(p.needsLegalReview)
});

const mapFramework = (raw: any): LearningFramework | null => {
  if (!raw || !Array.isArray(raw.programs)) return null;
  return {
    answers: { ...emptyAnswers(), ...(raw.answers || {}) },
    intro: typeof raw.intro === 'string' ? raw.intro : '',
    programs: raw.programs.map(mapProgram),
    source: raw.source === 'elliott' ? 'elliott' : 'engine',
    generatedAt: typeof raw.generatedAt === 'number' ? raw.generatedAt : Date.now(),
    added: (Array.isArray(raw.added) ? raw.added : []).map((a: any) => ({
      recommendationId: String(a.recommendationId),
      groupId: String(a.groupId || ''),
      name: a.name
    }))
  };
};

const unwrap = (res: any) => res?.data?.data ?? res?.data ?? {};

/**
 * The recommended description for a program that was added from the org's
 * Learning Framework, found through the recorded recommendation → program link.
 * The builder pre-fills it, so the admin starts from what Elliott recommended
 * the program should include. Resolves '' when the program didn't come from the
 * framework, the admin can't read it (program admins), or it can't be reached.
 */
export const fetchRecommendedDescription = async ({
  groupId,
  orgId,
  isPlatformAdmin
}: {
  groupId: string;
  orgId?: string;
  isPlatformAdmin?: boolean;
}): Promise<string> => {
  if (!groupId) return '';
  let fw: LearningFramework | null = null;
  try {
    const res: any = await API.getLearningFramework({ ...getLLAuthHeaders(), orgId: isPlatformAdmin ? orgId : undefined });
    fw = mapFramework(unwrap(res).framework);
  } catch {
    fw = readLocal(orgId);
  }
  const link = fw?.added.find((a) => a.groupId === groupId);
  return (link && fw?.programs.find((p) => p.id === link.recommendationId)?.description) || '';
};

export const useLearningFrameworkStore = ({ enabled, orgId, isPlatformAdmin, orgName }: Options): LearningFrameworkStore => {
  const [framework, setFramework] = useState<LearningFramework | null>(null);
  const [loading, setLoading] = useState(false);
  const [storage, setStorage] = useState<'server' | 'local'>('server');
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Platform admins browse another org: the BE needs it named on the query.
  const auth = useCallback(
    () => ({ ...getLLAuthHeaders(), orgId: isPlatformAdmin ? orgId : undefined }),
    [isPlatformAdmin, orgId]
  );

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res: any = await API.getLearningFramework(auth());
      if (!alive.current) return;
      setFramework(mapFramework(unwrap(res).framework));
      setStorage('server');
    } catch (e: any) {
      // BE unreachable (or the route isn't deployed yet): the browser copy.
      console.warn('[LearningFramework] load from server failed, using local copy', e?.response?.status || e?.message);
      if (!alive.current) return;
      setFramework(readLocal(orgId));
      setStorage('local');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [enabled, auth, orgId]);

  useEffect(() => {
    if (!enabled) {
      setFramework(null);
      return;
    }
    void reload();
  }, [enabled, reload]);

  const generate = useCallback<LearningFrameworkStore['generate']>(
    async (answers) => {
      // 1. Elliott live. Any failure (timeout, 502 malformed, 429 limit, 403
      //    switched off, route missing) falls through to the engine — never
      //    an error screen.
      try {
        const res: any = await API.recommendLearningFramework({ answers: answers as any, ...auth() });
        const fw = mapFramework(unwrap(res).framework);
        if (fw) {
          if (alive.current) {
            setFramework(fw);
            setStorage('server');
          }
          writeLocal(orgId, null);
          return fw;
        }
      } catch (e: any) {
        console.warn('[LearningFramework] live recommendation unavailable, using built-in engine', e?.response?.status || e?.message);
      }
      // 2. Built-in engine, shared via PUT when the BE is there.
      const fallback: LearningFramework = {
        answers,
        intro: '',
        programs: recommend(answers, orgName),
        source: 'engine',
        generatedAt: Date.now(),
        added: []
      };
      try {
        const res: any = await API.saveLearningFramework({
          body: { answers: answers as any, intro: '', programs: fallback.programs },
          ...auth()
        });
        const saved = mapFramework(unwrap(res).framework) || fallback;
        if (alive.current) {
          setFramework(saved);
          setStorage('server');
        }
        writeLocal(orgId, null);
        return saved;
      } catch (e: any) {
        console.warn('[LearningFramework] save to server failed, keeping a local copy', e?.response?.status || e?.message);
        writeLocal(orgId, fallback);
        if (alive.current) {
          setFramework(fallback);
          setStorage('local');
        }
        return fallback;
      }
    },
    [auth, orgId, orgName]
  );

  const remove = useCallback(async () => {
    try {
      await API.deleteLearningFramework(auth());
    } catch (e: any) {
      console.warn('[LearningFramework] delete on server failed', e?.response?.status || e?.message);
    }
    writeLocal(orgId, null);
    if (alive.current) setFramework(null);
  }, [auth, orgId]);

  const markAdded = useCallback<LearningFrameworkStore['markAdded']>(
    async (items) => {
      if (!items.length) return;
      // Optimistic: the tiles flip to "Added" now; the BE's list wins after.
      const merge = (fw: LearningFramework | null): LearningFramework | null => {
        if (!fw) return fw;
        const known = new Set(fw.added.map((a) => a.recommendationId));
        return { ...fw, added: [...fw.added, ...items.filter((i) => !known.has(i.recommendationId))] };
      };
      setFramework((prev) => {
        const next = merge(prev);
        if (storage === 'local') writeLocal(orgId, next);
        return next;
      });
      if (storage === 'local') return;
      try {
        const res: any = await API.markLearningFrameworkAdded({ items, ...auth() });
        const added = unwrap(res).added;
        if (Array.isArray(added) && alive.current) {
          setFramework((prev) => (prev ? { ...prev, added: added.map((a: any) => ({ recommendationId: String(a.recommendationId), groupId: String(a.groupId || ''), name: a.name })) } : prev));
        }
      } catch (e: any) {
        console.warn('[LearningFramework] recording added programs failed', e?.response?.status || e?.message);
      }
    },
    [auth, orgId, storage]
  );

  return { framework, loading, storage, reload, generate, remove, markAdded };
};

export default useLearningFrameworkStore;
